import React from 'react';
import classNames from 'clsx';
import {IconRebuilder} from '../IconRebuilder/IconRebuilder';
import {SvgIcon} from '../SvgIcon/SvgIcon';

export interface IconProps {
  count?: number;
  s: {[key: string]: string};
  iconId: number;
  text?: string;
  containerHeight: number;
  containerWidth: number;
  aspectRatio?: number;
  fixCartCountOverlap?: boolean;
}

export const Icon1 = (props: IconProps) => (
  <SvgIcon viewBox="0 0 105.5 126.1" preserveAspectRatio={true} {...props}>
    <path d="M102.143 118.16L93.812 48.2067C93.386 44.66 90.3566 42 86.7591 42H79.1382V56H74.4047V42H31.8032V56H27.0697V42H19.4488C15.8513 42 12.8219 44.66 12.3959 48.16L4.06489 118.16C3.78088 120.167 4.44357 122.173 5.76895 123.667C7.14167 125.16 9.0824 126 11.0705 126H95.1374C97.1255 126 99.0662 125.16 100.439 123.667C101.764 122.173 102.427 120.167 102.143 118.16Z" />
    <path d="M32.0594 25.6667C32.0594 14.0933 41.506 4.66667 53.1039 4.66667C64.7018 4.66667 74.1485 14.0933 74.1485 25.6667V42H78.825V25.6667C78.825 11.5267 67.2739 0 53.1039 0C38.9339 0 27.3828 11.5267 27.3828 25.6667V42H32.0594V25.6667Z" />
    {props.count !== undefined && (
      <text x="53" y="85.5" dy=".35em" textAnchor="middle" className={props.s.quantity} data-hook="items-count">
        {props.count}
      </text>
    )}
  </SvgIcon>
);

export const Icon2 = (props: IconProps) => (
  <SvgIcon viewBox="0 0 197.7 166" preserveAspectRatio={true} {...props}>
    <path
      d="M79.3913 147.956C79.3913 141.977 74.5443 137.13 68.5652 137.13C62.5861 137.13 57.7391 141.977 57.7391 147.956C57.7391 153.935 62.5861 158.782 68.5652 158.782C74.5443 158.782 79.3913 153.935 79.3913 147.956ZM86.6087 147.956C86.6087 157.921 78.5303 166 68.5652 166C58.6001 166 50.5217 157.921 50.5217 147.956C50.5217 137.991 58.6001 129.913 68.5652 129.913C78.5303 129.913 86.6087 137.991 86.6087 147.956Z"
      fill="black"
    />
    <path
      d="M144.348 147.956C144.348 141.977 139.501 137.13 133.522 137.13C127.543 137.13 122.696 141.977 122.696 147.956C122.696 153.935 127.543 158.782 133.522 158.782C139.501 158.782 144.348 153.935 144.348 147.956ZM151.565 147.956C151.565 157.921 143.487 166 133.522 166C123.557 166 115.478 157.921 115.478 147.956C115.478 137.991 123.557 129.913 133.522 129.913C143.487 129.913 151.565 137.991 151.565 147.956Z"
      fill="black"
    />
    <path
      d="M19.1289 0C24.4107 0.00134637 28.9694 3.78942 29.8352 9.09222H29.8281L48.4496 120.877C49.3148 126.065 53.8766 129.913 59.1135 129.913H137.13V137.13H59.1135C50.3492 137.13 42.7757 130.731 41.3308 122.061L22.7094 10.2622V10.2552C22.4207 8.48681 20.9177 7.21872 19.1289 7.21739H0V0H19.1289Z"
      fill="black"
    />
    <path
      d="M158.783 84.156V28.8696H166V84.156C165.999 89.453 162.204 93.8928 157.021 94.7706L157.028 94.7847L40.3229 115.351L39.0684 108.247L155.773 87.673L155.808 87.666C157.538 87.3772 158.782 85.921 158.783 84.156Z"
      fill="black"
    />
    {props.count !== undefined && (
      <text
        data-hook="items-count"
        className={classNames(props.s.quantity, props.s.withoutBackground)}
        textAnchor="middle"
        x="95"
        y="25"
        dy=".48em">
        {props.count}
      </text>
    )}
  </SvgIcon>
);

export const Icon3 = (props: IconProps) => (
  <IconRebuilder
    initialViewBoxWidth={120}
    initialViewBoxHeight={105}
    text={props.text}
    aspectRatio={props.aspectRatio!}>
    {({width, height, textRef, textLength}) => (
      <SvgIcon viewBox={`0 0 ${width} ${height}`} preserveAspectRatio={true} {...props}>
        <g data-hook="movable" transform={`translate(${textLength})`}>
          <circle className={props.s.bubble} cx="70" cy="50" r="50" />
          {props.count !== undefined && (
            <text x="70" y="50%" dy=".32em" textAnchor="middle" className={props.s.quantity} data-hook="items-count">
              {props.count}
            </text>
          )}
        </g>
        <text x="0" y="50%" dy=".35em" ref={textRef} data-hook="free-text">
          {props.text}
        </text>
      </SvgIcon>
    )}
  </IconRebuilder>
);

export const Icon4 = (props: IconProps) => {
  return (
    <IconRebuilder
      initialViewBoxWidth={145}
      initialViewBoxHeight={105}
      text={props.text}
      count={props.count}
      aspectRatio={props.aspectRatio!}>
      {({width, height, textRef}) => (
        <SvgIcon viewBox={`0 0 ${width} ${height}`} preserveAspectRatio={true} {...props}>
          <text x="0" y="83" ref={textRef} data-hook="free-text">
            {`${props.text!} `}
            {props.count !== undefined && (
              <tspan className={classNames(props.s.quantity, props.s.withoutBackground)} data-hook="items-count">
                ({props.count})&rlm;
              </tspan>
            )}
          </text>
        </SvgIcon>
      )}
    </IconRebuilder>
  );
};

export const Icon5 = (props: IconProps) => (
  <IconRebuilder
    initialViewBoxWidth={315}
    initialViewBoxHeight={129}
    aspectRatio={props.aspectRatio!}
    fixCartCountOverlap={props.fixCartCountOverlap}
    text={props.text}>
    {({width, height, textRef, textLength}) => (
      <SvgIcon viewBox={`0 0 ${width} ${height}`} preserveAspectRatio={true} {...props}>
        <g>
          <path
            d="M55.8836 113.85C55.8836 109.771 52.579 106.466 48.5 106.466C44.421 106.466 41.1164 109.771 41.1164 113.85C41.1164 117.929 44.421 121.233 48.5 121.233C52.579 121.233 55.8836 117.929 55.8836 113.85ZM63 113.85C63 121.857 56.5069 128.35 48.5 128.35C40.4931 128.35 34 121.857 34 113.85C34 105.843 40.4931 99.3496 48.5 99.3496C56.5069 99.3496 63 105.843 63 113.85Z"
            fill="black"
          />
          <path
            d="M110.884 113.85C110.884 109.771 107.579 106.466 103.5 106.466C99.421 106.466 96.1164 109.771 96.1164 113.85C96.1164 117.929 99.421 121.233 103.5 121.233C107.579 121.233 110.884 117.929 110.884 113.85ZM118 113.85C118 121.857 111.507 128.35 103.5 128.35C95.4931 128.35 89 121.857 89 113.85C89 105.843 95.4931 99.3496 103.5 99.3496C111.507 99.3496 118 105.843 118 113.85Z"
            fill="black"
          />
          <path
            d="M15.5602 0.349609C19.5866 0.350686 23.0754 2.78919 24.4199 6.41373L24.6592 7.15472L24.6648 7.17652L30.3858 28.2457H120.586C126.901 28.2462 131.387 34.227 129.607 40.1015L129.601 40.1124L117.124 80.7633C115.232 87.0631 109.333 91.3496 102.633 91.3496H51.461C44.6025 91.3496 38.6244 86.8461 36.8803 80.3982L17.5136 9.06168L17.3967 8.74567C17.0741 8.04625 16.3881 7.60789 15.5602 7.60694H0V0.349609H15.5602ZM44.0427 78.5349V78.5403C44.9229 81.7959 47.9508 84.0977 51.461 84.0977H102.633C106.076 84.0977 109.066 81.905 110.023 78.7201L110.029 78.6983L122.506 38.0256C122.879 36.7642 121.901 35.5036 120.586 35.503H32.3558L44.0427 78.5349Z"
            fill="black"
          />
        </g>
        <text x="174" y="95" ref={textRef} data-hook="free-text">
          {props.text}
        </text>
        <g data-hook="movable" transform={`translate(${textLength})`}>
          {props.count !== undefined && (
            <text
              x="202"
              y="95"
              textAnchor="start"
              className={classNames(props.s.quantity, props.s.withoutBackground)}
              data-hook="items-count">
              {props.count}
            </text>
          )}
        </g>
      </SvgIcon>
    )}
  </IconRebuilder>
);

export const Icon6 = (props: IconProps) => (
  <SvgIcon viewBox="0 0 329.7 134.5" preserveAspectRatio={false} {...props}>
    <g clipPath="url(#clip0_802_996)">
      <path
        d="M249.6 2.99999C285.3 13.7 305.6 50.6 294.8 86.3C284 122 247.2 142.3 211.5 131.5C175.8 120.8 155.5 83.9 166.3 48.2C177 13.1 213.9 -7.20001 249.6 2.99999Z"
        fill="black"
      />
      <path
        d="M58.4628 119.088C58.4628 114.821 55.0057 111.364 50.7385 111.364C46.4712 111.364 43.0141 114.821 43.0141 119.088C43.0141 123.355 46.4712 126.813 50.7385 126.813C55.0057 126.813 58.4628 123.355 58.4628 119.088ZM65.9077 119.088C65.9077 127.465 59.115 134.257 50.7385 134.257C42.362 134.257 35.5692 127.465 35.5692 119.088C35.5692 110.712 42.362 103.919 50.7385 103.919C59.115 103.919 65.9077 110.712 65.9077 119.088Z"
        fill="black"
      />
      <path
        d="M116.001 119.088C116.001 114.821 112.544 111.364 108.277 111.364C104.01 111.364 100.553 114.821 100.553 119.088C100.553 123.355 104.01 126.813 108.277 126.813C112.544 126.813 116.001 123.355 116.001 119.088ZM123.446 119.088C123.446 127.465 116.653 134.257 108.277 134.257C99.9004 134.257 93.1077 127.465 93.1077 119.088C93.1077 110.712 99.9004 103.919 108.277 103.919C116.653 103.919 123.446 110.712 123.446 119.088Z"
        fill="black"
      />
      <path
        d="M16.2784 0.349609C20.4906 0.350736 24.1404 2.90179 25.547 6.69362L25.7973 7.4688L25.8032 7.4916L31.7882 29.5332H126.151C132.758 29.5338 137.451 35.7906 135.589 41.9362L135.583 41.9476L122.53 84.4747C120.55 91.0652 114.379 95.5496 107.37 95.5496H53.8362C46.6611 95.5496 40.4071 90.8382 38.5825 84.0928L18.3219 9.46377L18.1996 9.13318C17.8621 8.40147 17.1444 7.94289 16.2784 7.9419H0V0.349609H16.2784ZM46.0754 82.1434V82.1491C46.9962 85.555 50.1639 87.963 53.8362 87.963H107.37C110.972 87.963 114.1 85.6691 115.101 82.3372L115.107 82.3144L128.16 39.7645C128.55 38.4449 127.527 37.1261 126.151 37.1255H33.8492L46.0754 82.1434Z"
        fill="black"
      />
    </g>
    <defs>
      <clipPath id="clip0_802_996">
        <rect width="329.7" height="134.5" fill="white" />
      </clipPath>
    </defs>
    {props.count !== undefined && (
      <text x="230" y="70" dy=".35em" textAnchor="middle" className={props.s.quantity} data-hook="items-count">
        {props.count}
      </text>
    )}
  </SvgIcon>
);

export const Icon7 = (props: IconProps) => (
  <IconRebuilder
    initialViewBoxWidth={140}
    initialViewBoxHeight={200}
    text={props.text}
    aspectRatio={props.aspectRatio!}>
    {({width, height, textLength, textRef}) => (
      <SvgIcon viewBox={`0 0 ${width} ${height}`} preserveAspectRatio={false} {...props}>
        <g data-hook="movable" transform={`translate(${textLength})`}>
          <path
            className={props.s.bubble}
            d="M88.7 4.121c-35.7-10.7-73.2 10.1-83.3 45.2 -5.4 17.9-2.4 36.9 6 51.8l-7.7 26.2 25-6c6.5 5.4 14.3 8.9 22.6 11.3 35.7 10.7 73.2-10.1 83.3-45.2C144.6 51.721 124.4 14.221 88.7 4.121z"
          />
          {props.count !== undefined && (
            <text x="70" y="70" dy=".35em" textAnchor="middle" className={props.s.quantity} data-hook="items-count">
              {props.count}
            </text>
          )}
        </g>
        <text x="0" y="178" data-hook="free-text" ref={textRef}>
          {props.text}
        </text>
      </SvgIcon>
    )}
  </IconRebuilder>
);

export const Icon8 = (props: IconProps) => (
  <SvgIcon viewBox="0 0 267 123" preserveAspectRatio={false} {...props}>
    <g clipPath="url(#clip0_802_878)">
      <path
        d="M117.125 48.8418C117.97 48.8418 118.774 49.2097 119.321 49.848C119.866 50.486 120.099 51.3302 119.961 52.1549L110.072 110.957C108.921 117.794 102.888 122.842 95.9051 122.842H29.0949C22.0461 122.842 16.0773 117.786 14.9276 110.957L5.03914 52.1549C4.90057 51.3302 5.13367 50.486 5.67927 49.848C6.22554 49.2097 7.0298 48.8418 7.87484 48.8418H117.125ZM20.599 110.023C21.2898 114.123 24.874 117.149 29.0949 117.149H95.9051C100.077 117.149 103.712 114.114 104.401 110.023L113.734 54.5341H11.2664L20.599 110.023Z"
        fill="black"
      />
      <path d="M57 1.60783L40.2878 51.8418L35 50.0758L51.7122 -0.158203L57 1.60783Z" fill="black" />
      <path d="M90 50.0758L84.9525 51.8418L69 1.60783L74.0475 -0.158203L90 50.0758Z" fill="black" />
      <path d="M125 48.8418V55.8418H0V48.8418H125Z" fill="black" />
      <path d="M57 65.8418V105.842H52V65.8418H57Z" fill="black" />
      <path d="M74 65.8418V105.842H69V65.8418H74Z" fill="black" />
      <path d="M91 65.8418V105.842H86V65.8418H91Z" fill="black" />
      <path d="M40 65.8418V105.842H35V65.8418H40Z" fill="black" />
    </g>
    <defs>
      <clipPath id="clip0_802_878">
        <rect width="267" height="123" fill="white" />
      </clipPath>
    </defs>
    {props.count !== undefined && (
      <text
        x="152"
        y="118"
        textAnchor="start"
        className={classNames(props.s.quantity, props.s.withoutBackground)}
        data-hook="items-count">
        {props.count}
      </text>
    )}
  </SvgIcon>
);

export const Icon9 = (props: IconProps) => (
  <SvgIcon viewBox="0 0 164.9 196.4" preserveAspectRatio={true} {...props}>
    {props.count !== undefined && (
      <text
        x="82"
        y="122"
        dy=".35em"
        textAnchor="middle"
        className={classNames(props.s.quantity, props.s.withoutBackground)}
        data-hook="items-count">
        {props.count}
      </text>
    )}
    <g clipPath="url(#clip0_800_867)">
      <path
        d="M154.264 51C160.763 51 165.857 56.6869 165.06 63.1777L165.067 63.1848L152.504 186.435C152.5 186.48 152.489 186.524 152.483 186.569C151.729 191.949 147.178 195.999 141.701 196H23.6622C18.1839 196 13.6275 191.95 12.8728 186.569C12.8666 186.525 12.8631 186.479 12.8586 186.435L0.21769 63.1848L0.224784 63.1777C-0.572237 56.6869 4.52226 51 11.0213 51H154.264ZM11.0213 58.25C9.11528 58.25 7.57282 59.726 7.41066 61.5422L7.43194 62.3352L7.43904 62.4485L20.0658 185.564L20.2218 186.237C20.7174 187.742 22.0993 188.75 23.6622 188.75H141.701C143.485 188.749 145.028 187.433 145.29 185.564L157.846 62.4485L157.853 62.3352C158.14 60.1759 156.441 58.25 154.264 58.25H11.0213Z"
        fill="black"
      />
      <path
        d="M114.961 40C114.961 21.9356 100.481 7.27273 82.6425 7.27273C64.8039 7.27273 50.3243 21.9356 50.3243 40V80H43.1425V40C43.1425 17.919 60.8374 0 82.6425 0C104.447 0 122.142 17.919 122.142 40V80H114.961V40Z"
        fill="black"
      />
    </g>
    <defs>
      <clipPath id="clip0_800_867">
        <rect width="164.9" height="196.4" fill="white" />
      </clipPath>
    </defs>
  </SvgIcon>
);

export const Icon10 = (props: IconProps) => (
  <SvgIcon viewBox="0 0 267.8 178.8" preserveAspectRatio={true} {...props}>
    <g clipPath="url(#clip0_802_1007)">
      <path
        d="M219.7 2.60008C255.4 13.3001 275.7 50.2001 264.9 85.9001C254.2 121.6 217.3 141.9 181.6 131.1C145.9 120.4 125.6 83.5001 136.4 47.8001C147 12.8001 184.5 -7.49992 219.7 2.60008Z"
        fill="black"
      />
      <path
        d="M141.636 89.5725H109.578L89.6109 30L83.0473 32.2013L102.255 89.5725H49.6764L68.8836 32.2013L62.32 30L42.3527 89.5725H0V96.4515H7.46182L18.8618 164.623C20.2436 172.946 27.4291 179 35.8582 179H116.142C124.571 179 131.756 172.946 133.138 164.623L144.538 96.4515H152V89.5725H141.636ZM110.545 110.21V158.363H103.636V110.21H110.545ZM89.8182 110.21V158.363H82.9091V110.21H89.8182ZM69.0909 110.21V158.363H62.1818V110.21H69.0909ZM48.3636 110.21V158.363H41.4545V110.21H48.3636Z"
        fill="black"
      />
    </g>
    <defs>
      <clipPath id="clip0_802_1007">
        <rect width="267.8" height="178.8" fill="white" />
      </clipPath>
    </defs>
    {props.count !== undefined && (
      <text x="200" y="70" dy=".35em" textAnchor="middle" className={props.s.quantity} data-hook="items-count">
        {props.count}
      </text>
    )}
  </SvgIcon>
);

export const Icon11 = (props: IconProps) => (
  <SvgIcon viewBox="0 0 136 134.5" preserveAspectRatio={true} {...props}>
    <path
      d="M87.1 3c35.7 10.7 56 47.6 45.2 83.3s-47.6 56-83.3 45.2C13.3 120.8-7 83.9 3.8 48.2 14.5 13.1 51.4-7.2 87.1 3z"
      className={props.s.bubble}></path>
    {props.count !== undefined && (
      <text x="70" y="70" dy=".35em" className={props.s.quantity} data-hook="items-count" textAnchor="middle">
        {props.count}
      </text>
    )}
  </SvgIcon>
);
